import React from 'react'
import { DonutChart, PieData } from '../../../components/charts/pieChart'
import { User } from '../../../providers/context'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../../request'
import { TeacherConferenceData, isLocationEmpty, ConferenceLocationEdit } from '../components/conferenceComponents'
import { IconBullet, IconCaretDown, IconCaretRight, IconReload, IconWarningCircle } from '../../../icons/library'
import { Tooltip } from '../../../components/simple/tooltip'
import { UserSelector } from '../../../components/userSelector/userSelector'
import { CustomRecipientMaker } from '../../../components/userSelector/customRecipientMaker'
import { StudentRecipient } from '../../home/compose/composeTypes'
import { loadStudentsByIdentifiers } from '../../../components/userSelector/userSelectorPlugins'
import { DelayedTextInput, TextArea } from '../../../components/form/textInput'
import { Toasts } from '../../../providers/toast'
import { Button, ButtonShape, ButtonStyle } from '../../../components/simple/button'
import { RemoteTable, VisibleColumnDef } from '../../../components/table'
import { Popups } from '../../../providers/popups'

type InviteResponseInfo = {
  originalInvites: string[]
  resentInvites: string[]
  notSentInvites: string[]
}

type Invite = {
  studentName: string
  teacherName: string
  lastInvited: string
  apptID: number
}

export const InviteTab: React.FC<{
  conference: TeacherConferenceData
  viewAsAdmin: boolean
  user: User
  editYourLocation: () => void
}> = ({ conference, viewAsAdmin, user, editYourLocation }) => {
  const { data: inviteStats } = useRequest<PieData>(
    '/frontend_api/getConferenceInvitePie',
    React.useMemo(() => ({ id: conference.id, viewAsAdmin }), [conference.id, viewAsAdmin])
  )

  const inviteDisabledMessage = !conference.active
    ? 'Invites are disabled because the conference is not active.'
    : viewAsAdmin
      ? !conference.adminLed
        ? 'You cannot invite to this conference as an admin.'
        : undefined
      : conference.waitingForAdminToInvite
        ? 'Invites are disabled until an admin at your school sends initial invites.'
        : isLocationEmpty(conference.yourLocation, conference.virtualOrInPerson)
          ? 'Set your location to invite families.'
          : undefined

  const [inviteRecipients, setInviteRecipients] = React.useState<StudentRecipient[]>([])

  const [inviteMessage, setInviteMessage] = React.useState<string>(conference.defaultInviteMessage)
  const [inviteLoading, setInviteLoading] = React.useState<boolean>(false)
  const [inviteInfo, setInviteInfo] = React.useState<InviteResponseInfo | undefined>(undefined)

  const lastMaxLengthPopupTime = React.useRef(0)

  const [inviteTableSearch, setInviteTableSearch] = React.useState<string>('')

  const [tableReload, setTableReload] = React.useState(0)
  const tableRemote = React.useMemo(
    () => ({
      url: '/frontend_api/getConferenceInvitesTable',
      data: {
        conferenceID: conference.id,
        viewAsAdmin,
      },
    }),
    [conference.id, viewAsAdmin, tableReload]
  )
  const reloadInvites = () => setTableReload((r) => r + 1)

  const inviteTableColumns = React.useMemo<VisibleColumnDef<Invite>[]>(
    () => [
      { id: 'studentName', header: 'Student Name', accessorKey: 'studentName' },
      { id: 'teacherName', header: 'Teacher Name', accessorKey: 'teacherName', show: viewAsAdmin },
      { id: 'lastInvited', header: 'Last Invited', accessorKey: 'lastInvited' },
      {
        id: 'reinvite',
        header: 'Resend',
        size: 65,
        show: conference.active,
        enableSorting: false,
        cell: ({ row }) => <ReinviteButton apptID={row.original.apptID} onReinvite={reloadInvites} />,
      },
    ],
    [conference]
  )

  const [reinviteAllLoading, setReinviteAllLoading] = React.useState(false)
  const reinviteAll = async () => {
    setReinviteAllLoading(true)
    if (
      await Popups.simpleConfirm(
        <b>Are you sure you want to reinvite all families?</b>,
        "This will only invite people who haven't been invited in the last day"
      )
    ) {
      try {
        const info = await makeRequest<InviteResponseInfo>('/frontend_api/conferenceReinviteAll', {
          conference_id: conference.id,
          adminView: viewAsAdmin,
        })
        setInviteInfo(info)
        reloadInvites()
      } catch (e) {
        showAxiosErrorAsToast(e)
      }
    }
    setReinviteAllLoading(false)
  }

  return (
    <div className="mt-2 flex min-h-0 flex-grow gap-8 overflow-y-scroll">
      <div className="flex min-w-[400px] max-w-[400px] flex-col gap-2">
        {!viewAsAdmin && <ConferenceLocationEdit conference={conference} editYourLocation={editYourLocation} />}
        <div className="mt-2 text-xl font-bold">Invite Families</div>
        {conference.adminLed && viewAsAdmin && <ConferenceAdminMissingLocationsRow conference={conference} />}
        <Tooltip text={inviteDisabledMessage}>
          <div className={`${inviteDisabledMessage ? 'opacity-50 *:pointer-events-none' : ''} flex flex-col gap-2`}>
            <InviteUserSelector
              inviteRecipients={inviteRecipients}
              setInviteRecipients={setInviteRecipients}
              conference={conference}
              viewAsAdmin={viewAsAdmin}
            />
            <div className="rounded-lg border border-gray-200 leading-5">
              <TextArea
                rows={3}
                className="!border-none !bg-transparent !pb-0 !outline-none"
                value={inviteMessage}
                onChange={(v) => {
                  if (v.length > 150) {
                    if (lastMaxLengthPopupTime.current + 1000 < Date.now()) {
                      lastMaxLengthPopupTime.current = Date.now()
                      Toasts.showToast({
                        message: 'Word limit hit! Need longer? Send a message through ReachMyTeach',
                        duration: 3000,
                      })
                    }
                    setInviteMessage(v.slice(0, 150))
                  } else setInviteMessage(v)
                }}
                placeholder={conference.defaultInviteMessage}
              />
              <div className="-mt-1 px-2 pb-1">
                <div className="text-link hover:underline">Schedule here</div>
                {!viewAsAdmin && <div>- {user.name}</div>}
              </div>
            </div>
          </div>
        </Tooltip>
        <div className="flex gap-2">
          <Button
            logo
            disabled={inviteRecipients.length === 0 || inviteLoading || !!inviteDisabledMessage}
            onClick={() => {
              setInviteLoading(true)
              makeRequest<InviteResponseInfo>('/frontend_api/sendConferenceInvites', {
                conference_id: conference.id,
                recipients: {
                  courses: inviteRecipients.filter((r) => !!r.group).map((r) => r.groupID),
                  people: inviteRecipients.filter((r) => !r.group).map((r) => r.id),
                },
                message: inviteMessage,
                adminInvite: viewAsAdmin,
              })
                .then((info) => {
                  setInviteInfo(info)
                  setInviteRecipients([])
                  reloadInvites()
                })
                .catch(showAxiosErrorAsToast)
                .finally(() => setInviteLoading(false))
            }}
          >
            Invite
          </Button>
        </div>
        {inviteInfo && <InviteInfo info={inviteInfo} />}
        {inviteStats && (
          <div className="mt-6">
            <DonutChart title="Invite Stats" legend={{ side: 'left' }} data={inviteStats} />
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-grow flex-col gap-2">
        <div className="flex items-center gap-4">
          <div className="text-xl font-bold">Pending Invites</div>
          <DelayedTextInput
            className="rounded-full"
            value={inviteTableSearch}
            onChange={setInviteTableSearch}
            placeholder="Search"
            clearable
          />
          {conference.active && (
            <Button
              logo
              disabled={reinviteAllLoading}
              className="ml-auto"
              shape={ButtonShape.icon}
              onClick={reinviteAll}
              tooltip="Resends a message to all families with pending invites"
            >
              <IconReload />
            </Button>
          )}
        </div>
        <RemoteTable
          columns={inviteTableColumns}
          remote={tableRemote}
          search={inviteTableSearch}
          sortable
          exportName={`All Invites for ${conference.name}`}
          pagination={15}
        />
      </div>
    </div>
  )
}

const ConferenceAdminMissingLocationsRow: React.FC<{ conference: TeacherConferenceData }> = ({ conference }) => {
  return (
    <Button
      buttonStyle={ButtonStyle.warning}
      className="self-stretch"
      link={`/ptc/adminConferenceVerification/exclusionClasses/${conference.id}`}
    >
      <IconWarningCircle /> Exclude classes and confirm teacher locations
    </Button>
  )
}

const ReinviteButton: React.FC<{ apptID: number; onReinvite: () => void }> = ({ apptID, onReinvite }) => {
  const [loading, setLoading] = React.useState(false)
  return (
    <Button
      disabled={loading}
      shape={ButtonShape.iconSmall}
      buttonStyle={ButtonStyle.warning}
      onClick={() => {
        setLoading(true)
        makeRequest('/ptc/reinvite', { appt_id: apptID })
          .then(onReinvite)
          .catch(showAxiosErrorAsToast)
          .finally(() => setLoading(false))
      }}
    >
      <IconReload />
    </Button>
  )
}

const InviteInfo: React.FC<{ info: InviteResponseInfo }> = ({ info }) => {
  const [expandOriginal, setExpandOriginal] = React.useState(false)
  const [expandResent, setExpandResent] = React.useState(false)
  const [expandNotSent, setExpandNotSent] = React.useState(false)
  React.useEffect(() => {
    setExpandOriginal(false)
    setExpandResent(false)
    setExpandNotSent(false)
  }, [info])
  return (
    <div className="flex flex-col gap-2 rounded bg-warning px-2 py-1">
      {info.originalInvites.length === 0 && info.resentInvites.length === 0 && info.notSentInvites.length === 0 && (
        <div>No invites sent</div>
      )}
      {info.originalInvites.length > 0 && (
        <>
          {info.originalInvites.length === 1 ? (
            <InviteBullet>
              <b>{info.originalInvites[0]}'s</b> family has been invited to this conference
            </InviteBullet>
          ) : (
            <InviteInfoToggle expanded={expandOriginal} setExpanded={setExpandOriginal} rows={info.originalInvites}>
              <b>{info.originalInvites.length}</b> families have been invited to this conference
            </InviteInfoToggle>
          )}
        </>
      )}
      {info.resentInvites.length > 0 && (
        <>
          {info.resentInvites.length === 1 ? (
            <InviteBullet>{info.resentInvites[0]}'s family has been re-invited to this conference</InviteBullet>
          ) : (
            <InviteInfoToggle expanded={expandResent} setExpanded={setExpandResent} rows={info.resentInvites}>
              <b>{info.resentInvites.length}</b> families have been re-invited to this conference
            </InviteInfoToggle>
          )}
        </>
      )}
      {info.notSentInvites.length > 0 && (
        <>
          {info.notSentInvites.length === 1 ? (
            <InviteBullet>
              <b>{info.notSentInvites[0]}'s</b> family has already been invited, has scheduled an appointment for this
              conference, or has no current classes.
            </InviteBullet>
          ) : (
            <InviteInfoToggle expanded={expandNotSent} setExpanded={setExpandNotSent} rows={info.notSentInvites}>
              <div>
                <b>{info.notSentInvites.length}</b> invites were not sent because these families have already been
                invited, have scheduled an appointment for this conference, or have no current classes
              </div>
            </InviteInfoToggle>
          )}
        </>
      )}
    </div>
  )
}

const InviteBullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex cursor-pointer items-start gap-1">
    <IconBullet className="-ml-1 mt-1 scale-[.4]" />
    <div>{children}</div>
  </div>
)

const InviteInfoToggle: React.FC<{
  expanded: boolean
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>
  children: React.ReactNode
  rows: string[]
}> = ({ expanded, setExpanded, children, rows }) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex cursor-pointer items-start gap-1" onClick={() => setExpanded((e) => !e)}>
        {expanded ? <IconCaretDown className="mt-1" /> : <IconCaretRight className="mt-1" />}
        {children}
      </div>
      {expanded && (
        <ul className="list-inside list-disc">
          {rows.slice(0, 50).map((r) => (
            <li key={r} className="pl-2">
              {r}
            </li>
          ))}
          {rows.length > 50 && <li className="pl-2">And {rows.length - 50} more</li>}
        </ul>
      )}
    </div>
  )
}

const InviteUserSelector: React.FC<{
  inviteRecipients: StudentRecipient[]
  setInviteRecipients: React.Dispatch<React.SetStateAction<StudentRecipient[]>>
  conference: TeacherConferenceData
  viewAsAdmin: boolean
}> = ({ inviteRecipients, setInviteRecipients, conference, viewAsAdmin }) => {
  const customSections = React.useMemo(
    () =>
      viewAsAdmin
        ? [
            {
              title: 'Select a group',
              body: (closeSection: () => void) => (
                <CustomRecipientMaker
                  schools={[conference.school]}
                  grades={conference.school.grades.map((grade) => ({ grade, schoolIDs: [conference.school.id] }))}
                  addCustomRecipient={(recipient) => {
                    closeSection()
                    setInviteRecipients((sr) => {
                      if (sr.some((r) => r.group && r.groupID === recipient.groupID)) return sr
                      return [...sr, recipient]
                    })
                  }}
                />
              ),
            },
          ]
        : [],
    [viewAsAdmin, conference]
  )

  const fetchUsersByIdentifiersByIdentifiers = React.useMemo(
    () => loadStudentsByIdentifiers([conference.school.id]),
    []
  )

  const searchData = React.useMemo(() => ({ conferenceID: conference.id }), [conference.id])

  return (
    <UserSelector<StudentRecipient>
      selectedUsers={inviteRecipients}
      setSelectedUsers={setInviteRecipients}
      customSections={customSections}
      fetchUsersByIdentifiersForPaste={fetchUsersByIdentifiersByIdentifiers}
      searchData={searchData}
      url="/frontend_api/searchConferenceInviteStudents"
      loadedUsers={conference.preloadedRecipients}
      userDescriptor="students"
    />
  )
}
